import {
  ANNOTATION_LOCATION,
  ANNOTATION_ORIGIN_LOCATION,
  ANNOTATION_SOURCE_LOCATION,
  Entity,
  getEntitySourceLocation,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { ApiProvider } from '@backstage/core-app-api';
import { ApiHolder, ApiRef, configApiRef, useApi } from '@backstage/core-plugin-api';
import { ScmAuth, scmAuthApiRef } from '@backstage/integration-react';
import { catalogApiRef, EntityProvider, useEntity } from '@backstage/plugin-catalog-react';
import { GithubActionsClient, githubActionsApiRef } from '@backstage-community/plugin-github-actions';
import { GithubPullRequestsClient, githubPullRequestsApiRef } from '@roadiehq/backstage-plugin-github-pull-requests';
import { emuAuthApiRef } from '@runway/devkit';
import { ReactNode, useEffect, useMemo, useState } from 'react';

export const GITHUB_PROJECT_SLUG_ANNOTATION = 'github.com/project-slug';

export enum GithubEntityOrigin {
  EMU = 'emu',
  GHEC = 'ghec',
  UNKNOWN = 'unknown',
}

type GithubEntityRouting = {
  origin: GithubEntityOrigin;
  projectSlug?: string;
};

const githubLocationAnnotations = [ANNOTATION_ORIGIN_LOCATION, ANNOTATION_SOURCE_LOCATION, ANNOTATION_LOCATION];

const parseGithubProjectSlug = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  // Strip Backstage location ref prefixes (e.g. `url:`, `github:`, `bitbucket:`)
  // that wrap an http(s) URL, so `new URL()` receives a valid absolute URL.
  const sanitizedValue = value.replace(/^[a-z][a-z0-9+.-]*:(https?:\/\/)/, '$1');

  const slugMatch = sanitizedValue.match(/^([^/]+)\/([^/]+)$/);
  if (slugMatch) {
    return `${slugMatch[1]}/${slugMatch[2].replace(/\.git$/, '')}`;
  }

  try {
    const parsedUrl = new URL(sanitizedValue);

    if (parsedUrl.hostname !== 'github.com') {
      return undefined;
    }

    const [owner, repo] = parsedUrl.pathname.split('/').filter(Boolean);

    if (!owner || !repo) {
      return undefined;
    }

    return `${owner}/${repo.replace(/\.git$/, '')}`;
  } catch {
    return undefined;
  }
};

const parseEmuOrg = (emuOrgUrl?: string): string | undefined => {
  if (!emuOrgUrl) {
    return undefined;
  }

  const sanitizedValue = emuOrgUrl.replace(/\/\*\*$/, '');

  try {
    const parsedUrl = new URL(sanitizedValue);
    return parsedUrl.pathname.split('/').filter(Boolean)[0];
  } catch {
    return undefined;
  }
};

const getLocationProjectSlug = (entity: Entity): string | undefined => {
  return githubLocationAnnotations
    .map(annotationName => parseGithubProjectSlug(entity.metadata.annotations?.[annotationName]))
    .find(Boolean);
};

// NOTE: resolveGithubEntityRouting is primarily used for testing and simple cases.
// In RoutedGithubEntityContent, routing is resolved inline with catalogApi location data.
export const resolveGithubEntityRouting = (entity: Entity, emuOrgUrl?: string): GithubEntityRouting => {
  const annotationProjectSlug = parseGithubProjectSlug(entity.metadata.annotations?.[GITHUB_PROJECT_SLUG_ANNOTATION]);
  const locationProjectSlug = getLocationProjectSlug(entity);
  const emuOrg = parseEmuOrg(emuOrgUrl);
  const emuLocationProjectSlug =
    emuOrg && locationProjectSlug?.startsWith(`${emuOrg}/`) ? locationProjectSlug : undefined;
  const emuAnnotationProjectSlug =
    emuOrg && annotationProjectSlug?.startsWith(`${emuOrg}/`) ? annotationProjectSlug : undefined;

  if (emuLocationProjectSlug || emuAnnotationProjectSlug) {
    return {
      origin: GithubEntityOrigin.EMU,
      projectSlug: emuLocationProjectSlug ?? emuAnnotationProjectSlug,
    };
  }

  if (annotationProjectSlug) {
    return {
      origin: GithubEntityOrigin.GHEC,
      projectSlug: annotationProjectSlug,
    };
  }

  if (locationProjectSlug) {
    return {
      origin: GithubEntityOrigin.GHEC,
      projectSlug: locationProjectSlug,
    };
  }

  return {
    origin: GithubEntityOrigin.UNKNOWN,
  };
};

export const getGithubRoutedEntity = (entity: Entity, emuOrgUrl?: string): Entity => {
  const routing = resolveGithubEntityRouting(entity, emuOrgUrl);
  const currentProjectSlug = entity.metadata.annotations?.[GITHUB_PROJECT_SLUG_ANNOTATION];

  // Only rewrite when the entity already has the annotation — prevents
  // injecting a github.com/project-slug into non-GitHub entities whose
  // location annotation happens to be hosted on GitHub.
  if (!routing.projectSlug || !currentProjectSlug || currentProjectSlug === routing.projectSlug) {
    return entity;
  }

  return {
    ...entity,
    metadata: {
      ...entity.metadata,
      annotations: {
        ...entity.metadata.annotations,
        [GITHUB_PROJECT_SLUG_ANNOTATION]: routing.projectSlug,
      },
    },
  };
};

type RoutedGithubEntityContentProps = {
  children: ReactNode;
  fallback?: ReactNode;
  isAvailable?: (entity: Entity) => boolean;
};

export const RoutedGithubEntityContent = ({
  children,
  fallback = null,
  isAvailable,
}: RoutedGithubEntityContentProps) => {
  const configApi = useApi(configApiRef);
  const emuAuthApi = useApi(emuAuthApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { entity } = useEntity();
  const emuOrgUrl = configApi.getOptionalString('catalog.emuOrgUrl');

  // Fetch the entity from the catalog API to get authoritative location data,
  // rather than depending on the potentially stale context entity's annotations.
  const entityRef = useMemo(() => stringifyEntityRef(entity), [entity]);
  const [catalogEntity, setCatalogEntity] = useState<Entity | undefined>();
  const [catalogLocation, setCatalogLocation] = useState<{ target: string } | undefined>();
  const [isResolvingOrigin, setIsResolvingOrigin] = useState(true);

  useEffect(() => {
    setIsResolvingOrigin(true);
    catalogApi
      .getEntityByRef(entityRef)
      .then(async fetchedEntity => {
        if (!fetchedEntity) {
          setCatalogEntity(undefined);
          setCatalogLocation(undefined);
          return;
        }
        setCatalogEntity(fetchedEntity);
        // Use the entity's source location annotation directly instead of
        // calling catalogApi.getLocationByRef() which may fail or be unavailable.
        // The entity's annotations already contain the authoritative location data.
        try {
          const sourceLocation = getEntitySourceLocation(fetchedEntity);
          setCatalogLocation({ target: sourceLocation.target });
        } catch {
          // Fallback: Try to get location from annotations directly
          const locationUrl = githubLocationAnnotations
            .map(annotationName => fetchedEntity.metadata.annotations?.[annotationName])
            .find(Boolean);

          if (locationUrl) {
            setCatalogLocation({ target: locationUrl });
          } else {
            setCatalogLocation(undefined);
          }
        }
      })
      .catch(() => {
        setCatalogEntity(undefined);
        setCatalogLocation(undefined);
      })
      .finally(() => {
        setIsResolvingOrigin(false);
      });
  }, [catalogApi, entityRef]);

  const resolvedEntity = catalogEntity ?? entity;

  // Resolve routing using catalog API location as the authoritative source,
  // falling back to entity annotations when catalog location is unavailable.
  const routing = useMemo(() => {
    const emuOrg = parseEmuOrg(emuOrgUrl);

    if (catalogLocation) {
      const catalogProjectSlug = parseGithubProjectSlug(catalogLocation.target);
      if (catalogProjectSlug) {
        const isEmuLocation = emuOrg && catalogProjectSlug.startsWith(`${emuOrg}/`);
        return {
          origin: isEmuLocation ? GithubEntityOrigin.EMU : GithubEntityOrigin.GHEC,
          projectSlug: catalogProjectSlug,
        };
      }
    }

    // Fallback: entity annotation if catalog location is unavailable or non-GitHub
    const annotationProjectSlug = parseGithubProjectSlug(
      resolvedEntity.metadata.annotations?.[GITHUB_PROJECT_SLUG_ANNOTATION],
    );
    if (annotationProjectSlug) {
      const isEmuAnnotation = emuOrg && annotationProjectSlug.startsWith(`${emuOrg}/`);
      return {
        origin: isEmuAnnotation ? GithubEntityOrigin.EMU : GithubEntityOrigin.GHEC,
        projectSlug: annotationProjectSlug,
      };
    }

    return {
      origin: GithubEntityOrigin.UNKNOWN,
    };
  }, [catalogLocation, resolvedEntity, emuOrgUrl]);

  const routedEntity = useMemo(() => getGithubRoutedEntity(resolvedEntity, emuOrgUrl), [resolvedEntity, emuOrgUrl]);

  // Build an EMU-aware API holder once per EMU auth + config instance.
  // GithubActionsClient and GithubPullRequestsClient capture scmAuthApi in
  // their constructors, so overriding scmAuthApiRef alone is insufficient —
  // we must provide fresh client instances that use the EMU scmAuth.
  const emuApis = useMemo<ApiHolder>(() => {
    const emuScmAuth = ScmAuth.forGithub(emuAuthApi);
    const emuActionsClient = new GithubActionsClient({ configApi, scmAuthApi: emuScmAuth });
    const emuPrClient = new GithubPullRequestsClient({ configApi, scmAuthApi: emuScmAuth });
    return {
      get<T>(ref: ApiRef<T>): T | undefined {
        if (ref === (scmAuthApiRef as unknown as ApiRef<T>)) {
          return emuScmAuth as unknown as T;
        }
        if (ref === (githubActionsApiRef as unknown as ApiRef<T>)) {
          return emuActionsClient as unknown as T;
        }
        if (ref === (githubPullRequestsApiRef as unknown as ApiRef<T>)) {
          return emuPrClient as unknown as T;
        }
        return undefined;
      },
    };
  }, [emuAuthApi, configApi]);

  if (isAvailable && !isAvailable(routedEntity)) {
    return <>{fallback}</>;
  }

  if (isResolvingOrigin) {
    return <>{fallback}</>;
  }

  if (routing.origin === GithubEntityOrigin.EMU) {
    return (
      <ApiProvider apis={emuApis}>
        <EntityProvider entity={routedEntity}>{children}</EntityProvider>
      </ApiProvider>
    );
  }

  return <EntityProvider entity={routedEntity}>{children}</EntityProvider>;
};
